import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AppHeaderLayout from '../components/AppHeaderLayout';
import { iconButtonStyles } from '../styles/commonStyles';
import { BiMenu } from 'react-icons/bi';
import Spinner from '../components/Spinner';
import GalleryCard from '../components/Channel/GalleryCard';
import { useBookmarks } from '../contexts/BookmarksContext.jsx';
import UserLevelInfo from '../components/UserLevel/UserLevelInfo';
import { useUserLevel } from '../hooks/useUserLevel';


function Bookmarks() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
	const [reloadAttempts, setReloadAttempts] = useState(0);
	const user = useSelector(state => state.auth.user);
	const { bookmarks, loading, error, reloadBookmarks } = useBookmarks();
	const { userLevel, features, hasFeature, hasMinLevel, getFeatureInfo, loading: levelLoading } = useUserLevel();

	// Безопасная обработка закладок
	const safeBookmarks = Array.isArray(bookmarks)
		? bookmarks.filter(b => b && (b.id != null) && (b.image_relation_id != null))
		: [];

	// Обновляем закладки при переходе на страницу, если кеш был сброшен
	useEffect(() => {
		if (user && bookmarks.length === 0 && !loading && reloadAttempts < 3) {
			setReloadAttempts(prev => prev + 1);
			reloadBookmarks();
		}
	}, [user, bookmarks.length, loading, reloadAttempts]);

	// Сбрасываем счетчик попыток при смене пользователя
	useEffect(() => {
		setReloadAttempts(0);
	}, [user]);

	// Сбрасываем счетчик попыток при успешной загрузке
	useEffect(() => {
		if (bookmarks.length > 0) {
			setReloadAttempts(0);
		}
	}, [bookmarks.length]);

	const leftContent = (
		<div className="flex items-center gap-2">
			<button
				className={iconButtonStyles + " block sm:hidden mr-2"}
				onClick={() => setSidebarMenuOpen(true)}
				aria-label="Открыть меню"
			>
				<BiMenu className="text-2xl" />
			</button>
			<span className="font-semibold text-base">{t('tabs.bookmarks', 'Избранное')}</span>
		</div>
	);

	const handleView = (bookmark) => {
		if (!bookmark || bookmark.image_relation_id == null) return;
		navigate(`/bookmarks/${bookmark.image_relation_id}`);
	};

	return (
		<AppHeaderLayout
			leftContent={leftContent}
			rightContent={null}
			sidebarMenuOpen={sidebarMenuOpen}
			onSidebarMenuOpen={() => setSidebarMenuOpen(true)}
			onSidebarMenuClose={() => setSidebarMenuOpen(false)}
			title={t('tabs.bookmarks', 'Избранное')}
		>
			<div className="p-0 sm:p-4">
				{loading || levelLoading || !user ? (
					<div className="flex items-center justify-center py-8">
						<Spinner />
					</div>
				) : error ? (
					<div className="text-center py-8">
						<p className="text-red-600 dark:text-red-400">
							{t('bookmarks.loadError', 'Ошибка загрузки избранного')}
						</p>
					</div>
				) : !hasMinLevel(1) ? (
					<div className="p-4">
						<UserLevelInfo
							userLevel={userLevel}
							features={features}
							hasFeature={hasFeature}
							hasMinLevel={hasMinLevel}
							getFeatureInfo={getFeatureInfo}
						/>
					</div>
				) : safeBookmarks.length === 0 ? (
					<div className="text-center py-8">
						<div className="text-6xl mb-4">⭐</div>
						<h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">
							{t('bookmarks.title', 'Избранное')}
						</h1>
						<p className="text-gray-600 dark:text-gray-400 mb-6">
							{t('bookmarks.description', 'Здесь будут отображаться изображения, которые вы добавили в избранное')}
						</p>
						<div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6">
							<h2 className="text-lg font-semibold mb-3 text-yellow-900 dark:text-yellow-100">
								{t('bookmarks.features.title', 'Функциональность')}
							</h2>
							<ul className="text-left space-y-2 text-yellow-800 dark:text-yellow-200">
								<li>• {t('bookmarks.features.view', 'Просмотр избранных изображений')}</li>
								<li>• {t('bookmarks.features.manage', 'Управление избранным')}</li>
								<li>• {t('bookmarks.features.quickAccess', 'Быстрый доступ к любимым изображениям')}</li>
							</ul>
						</div>
					</div>
				) : (
					<div className="space-y-4 pt-2.5">
						<div
							className="grid gap-2 sm:gap-3 px-2 sm:px-0"
							style={{
								gridTemplateColumns: 'repeat(auto-fill, 80px)',
								justifyContent: 'center'
							}}
						>
						{safeBookmarks.map(bookmark => (
							<GalleryCard
								key={bookmark.id || bookmark.image_relation_id}
								postcard={bookmark}
								onClick={() => handleView(bookmark)}
								className="w-[80px] h-[110px]"
							/>
						))}
						</div>
					</div>
				)}
			</div>
		</AppHeaderLayout>
	);
}

export default Bookmarks;
